/**
 * Reading Rio's manifest and the hiring view strictly, field by field: a
 * reply that isn't one is an error the page says plainly, never a half-drawn
 * player. Ids, hashes and links are checked for shape; everything else Rio
 * sends is ignored until it is read on purpose.
 */
import type {
  GrantedReplay,
  ManifestChunk,
  ReplayManifest,
  ReplaySignature,
  WorkReplayView,
} from './types';

type Obj = Record<string, unknown>;

const isObj = (value: unknown): value is Obj =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const str = (value: unknown, fallback = ''): string =>
  typeof value === 'string' ? value : fallback;

const strOrNull = (value: unknown): string | null =>
  typeof value === 'string' && value !== '' ? value : null;

const count = (value: unknown): number | null =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0
    ? value
    : null;

const SHA256_HEX = /^[0-9a-f]{64}$/i;

/** A link the player may fetch: https (the presigned S3 links), or blob (the development preview). */
export const isFetchable = (value: unknown): value is string =>
  typeof value === 'string' && /^(https:|blob:)/i.test(value);

const readChunk = (value: unknown): ManifestChunk | null => {
  if (!isObj(value)) return null;
  const n = count(value.n);
  const bytes = count(value.bytes);
  const sha256 = str(value.sha256);
  if (!value.kind || typeof value.kind !== 'string') return null;
  if (n === null || bytes === null || !SHA256_HEX.test(sha256)) return null;
  return { kind: value.kind, n, bytes, sha256: sha256.toLowerCase() };
};

/** The manifest, or null when it isn't one: every chunk must have a kind, a number, a size and a SHA-256. */
export const readManifest = (value: unknown): ReplayManifest | null => {
  if (!isObj(value) || !Array.isArray(value.chunks)) return null;
  const chunks = value.chunks.map(readChunk);
  if (chunks.some((c) => c === null)) return null;
  const final = isObj(value.final) ? value.final : {};
  const transcript = Array.isArray(value.transcript)
    ? value.transcript.flatMap((item) =>
        isObj(item) &&
        typeof item.t === 'number' &&
        Number.isFinite(item.t) &&
        typeof item.text === 'string'
          ? [{ t: item.t, text: item.text }]
          : [],
      )
    : [];
  return {
    assetId: str(value.assetId),
    taskId: str(value.taskId),
    language: str(value.language),
    chunks: chunks as ManifestChunk[],
    durationMs: count(value.durationMs) ?? 0,
    final: {
      files: Array.isArray(final.files) ? final.files.length : 0,
      passed: count(final.passed) ?? 0,
      failed: count(final.failed) ?? 0,
      ran: final.ran === true,
    },
    createdAt: strOrNull(value.createdAt),
    completedAt: strOrNull(value.completedAt),
    transcript,
  };
};

const readSignature = (value: unknown): ReplaySignature | null =>
  isObj(value) &&
  typeof value.value === 'string' &&
  value.value !== '' &&
  typeof value.algorithm === 'string'
    ? {
        keyId: str(value.keyId),
        algorithm: value.algorithm,
        value: value.value,
      }
    : null;

const readGranted = (value: unknown): GrantedReplay[] =>
  Array.isArray(value)
    ? value.flatMap((item) =>
        isObj(item) && typeof item.assetId === 'string' && item.assetId
          ? [
              {
                assetId: item.assetId,
                grantType: str(item.grantType),
                taskId: str(item.taskId),
                language: str(item.language),
                completedAt: strOrNull(item.completedAt),
              },
            ]
          : [],
      )
    : [];

/** The hiring view as avrio-backend passes it through, or null when the reply isn't one. */
export const readView = (value: unknown): WorkReplayView | null => {
  if (!isObj(value) || typeof value.assetId !== 'string' || !value.assetId)
    return null;
  const manifest = readManifest(value.manifest);
  if (!manifest) return null;
  const urls = Array.isArray(value.urls)
    ? value.urls.flatMap((item) => {
        if (!isObj(item) || typeof item.kind !== 'string') return [];
        const n = count(item.n);
        return n !== null && isFetchable(item.url)
          ? [{ n, kind: item.kind, url: item.url }]
          : [];
      })
    : [];
  return {
    assetId: value.assetId,
    grantType: str(value.grantType),
    manifest,
    manifestUrl: isFetchable(value.manifestUrl) ? value.manifestUrl : null,
    signature: readSignature(value.signature),
    urls,
    granted: readGranted(value.granted),
  };
};
